// i18n/index.ts — traduzioni. Ogni lingua è un file JSON in lingue/ (chiavi piatte, es. "home.slogan"),
// caricato solo quando serve: chi usa l'app in giapponese non scarica le altre 31 lingue.
// L'inglese è sempre caricato come ripiego. Il pannello /admin ha il suo spazio "admin" (it, en).

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./lingue/en.json";
import { eRtl, LINGUA_RIPIEGO, normalizzaLingua, type Lingua } from "../../../condiviso/lingue.ts";

const CARICATORI = import.meta.glob<{ default: Record<string, string> }>("./lingue/*.json");
const CARICATORI_ADMIN = import.meta.glob<{ default: Record<string, string> }>("./admin/*.json");

export const SPAZIO_APP = "app";
export const SPAZIO_ADMIN = "admin";
const LINGUA_RIPIEGO_ADMIN = "en";

export async function avviaI18n(lingua: Lingua): Promise<void> {
  await i18n.use(initReactI18next).init({
    lng: LINGUA_RIPIEGO,
    fallbackLng: LINGUA_RIPIEGO,
    ns: [SPAZIO_APP],
    defaultNS: SPAZIO_APP,
    resources: { [LINGUA_RIPIEGO]: { [SPAZIO_APP]: en } },
    keySeparator: false, // chiavi piatte: "home.slogan"
    interpolation: { escapeValue: false }, // React protegge già il testo
    returnNull: false,
  });
  await cambiaLingua(lingua);
}

async function caricaSpazio(
  lingua: string,
  spazio: string,
  caricatori: Record<string, () => Promise<{ default: Record<string, string> }>>,
): Promise<boolean> {
  if (i18n.hasResourceBundle(lingua, spazio)) return true;
  const carica = caricatori[`./${spazio === SPAZIO_ADMIN ? "admin" : "lingue"}/${lingua}.json`];
  if (!carica) return false;
  const modulo = await carica();
  i18n.addResourceBundle(lingua, spazio, modulo.default, true, true);
  return true;
}

export async function cambiaLingua(lingua: Lingua): Promise<void> {
  await caricaSpazio(lingua, SPAZIO_APP, CARICATORI);
  await i18n.changeLanguage(lingua);
  document.documentElement.lang = lingua;
  document.documentElement.dir = eRtl(lingua) ? "rtl" : "ltr";
}

/**
 * Carica le traduzioni del pannello: sempre l'inglese, più la lingua in uso se esiste (oggi: italiano).
 * Le chiavi che mancano nella lingua in uso si leggono in inglese.
 */
export async function caricaAdmin(): Promise<void> {
  await caricaSpazio(LINGUA_RIPIEGO_ADMIN, SPAZIO_ADMIN, CARICATORI_ADMIN);
  const lingua = normalizzaLingua(i18n.language) ?? LINGUA_RIPIEGO;
  if (lingua !== LINGUA_RIPIEGO_ADMIN) await caricaSpazio(lingua, SPAZIO_ADMIN, CARICATORI_ADMIN);
}

/**
 * v030: i nomi delle nostre lingue scritti da noi (alcuni browser non li hanno: sul telefono del proprietario il
 * bielorusso compariva come «Be»).
 */
export const NOMI_NATIVI: Readonly<Record<string, string>> = {
  ar: "العربية", be: "Беларуская", bg: "Български", bn: "বাংলা", cs: "Čeština", da: "Dansk", de: "Deutsch",
  el: "Ελληνικά", en: "English", "es-ES": "Español (España)", fi: "Suomi", fil: "Filipino", fr: "Français",
  hi: "हिन्दी", hr: "Hrvatski", hu: "Magyar", id: "Bahasa Indonesia", it: "Italiano", ja: "日本語", ko: "한국어",
  nl: "Nederlands", nb: "Norsk bokmål", pl: "Polski", "pt-PT": "Português (Portugal)", ro: "Română", ru: "Русский",
  sv: "Svenska", th: "ไทย", tr: "Türkçe", uk: "Українська", vi: "Tiếng Việt", "zh-Hans": "简体中文",
};

/** v030: la bandiera che rappresenta la lingua (file in public/bandiere, da flag-icons, licenza MIT). */
export const BANDIERE: Readonly<Record<string, string>> = {
  ar: "sa", be: "by", bg: "bg", bn: "bd", cs: "cz", da: "dk", de: "de", el: "gr", en: "gb", "es-ES": "es", fi: "fi",
  fil: "ph", fr: "fr", hi: "in", hr: "hr", hu: "hu", id: "id", it: "it", ja: "jp", ko: "kr", nl: "nl", nb: "no",
  pl: "pl", "pt-PT": "pt", ro: "ro", ru: "ru", sv: "se", th: "th", tr: "tr", uk: "ua", vi: "vn", "zh-Hans": "cn",
};

/** Il nome della lingua `lingua` scritto nella lingua `in` (per la seconda riga dell'elenco), o null. */
export function nomeIn(lingua: string, inLingua: string): string | null {
  try {
    const n = new Intl.DisplayNames([inLingua], { type: "language", fallback: "none" }).of(lingua);
    return n && n.toLowerCase() !== lingua.toLowerCase() ? n.charAt(0).toLocaleUpperCase(inLingua) + n.slice(1) : null;
  } catch {
    return null;
  }
}

/** Nome di una lingua scritto nella lingua stessa ("italiano", "日本語"). */
export function nomeNativo(lingua: string): string {
  if (NOMI_NATIVI[lingua]) return NOMI_NATIVI[lingua]!;
  try {
    const nome = new Intl.DisplayNames([lingua], { type: "language" }).of(lingua);
    if (!nome) return lingua;
    return nome.charAt(0).toLocaleUpperCase(lingua) + nome.slice(1);
  } catch {
    return lingua;
  }
}

export default i18n;
